import { Injectable } from '@angular/core';
import { Observable, of, switchMap, take, withLatestFrom } from 'rxjs';
import cloneDeep from 'lodash.clonedeep';
import { DatePipe } from '@angular/common';

import { DprStatusTracker } from '@shared/models/dpr';
import { UserDetailsFacade } from '@shared/state/facades';
import {
  DprStatusTrackerColumnValuesModel,
  DprStatusTrackerReportModel,
  DprStatusTrackerRowModel,
} from '@report/models/dpr-status-tracker-report.model';
import { StatusTrackerWithData } from '@shared/models/project-setup';
import { ColorType, DataType, SelectionItem } from '@shared/models';
import { buildBooleanValues, getValue } from '@shared/utils';

@Injectable()
export class ReportStatusTrackerService {
  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private datePipe: DatePipe
  ) {}

  constructStatusTrackerModel(
    dprStatusTracker: DprStatusTracker
  ): Observable<DprStatusTrackerReportModel[]> {
    return this.userDetailsFacade.query.projectSetup$.pipe(
      withLatestFrom(this.userDetailsFacade.query.colorTypes$),
      take(1),
      switchMap(([projectSetup, colorTypes]) => {
        const statusTrackerModels: DprStatusTrackerReportModel[] = [];
        const statusTrackerList = cloneDeep(
          projectSetup.statusTrackers
        ) as StatusTrackerWithData[];
        for (const statusTracker of statusTrackerList) {
          for (const columnGroup of statusTracker.columnGroups) {
            const columns = statusTracker.columns.filter(
              (x) => x.columnGroupId === columnGroup.id
            );

            if (
              !dprStatusTracker.dprStatusTrackerDetails.filter(
                (x) =>
                  x.statusTrackerId === statusTracker.id &&
                  columns.map((y) => y.id).includes(x.columnId)
              ).length
            ) {
              continue;
            }
            const model: DprStatusTrackerReportModel = {
              statusTrackerName: statusTracker.name,
              columnGroupName: columnGroup.name,
              columns,
              columnNumber: columns.length + 2,
              rows: this.constructDprStatusTrackerReportRows(
                dprStatusTracker,
                statusTracker,
                +columnGroup.id,
                colorTypes
              ),
            };

            statusTrackerModels.push(model);
          }
        }
        return of(statusTrackerModels);
      })
    );
  }

  private constructDprStatusTrackerReportRows(
    dprStatusTracker: DprStatusTracker,
    statusTrackerList: StatusTrackerWithData,
    columnGroupId: number,
    colorTypes: ColorType[]
  ): DprStatusTrackerRowModel[] {
    const rows: DprStatusTrackerRowModel[] = [];
    for (const row of statusTrackerList.rows) {
      const rowGroup = statusTrackerList.rowGroups.find(
        (x) => x.id === row.rowGroupId
      );

      const rowValue: DprStatusTrackerRowModel = {
        rowName: row.name,
        rowGroupName: rowGroup.name,
        rowSpan:
          rows.length && rows.at(rows.length - 1).rowGroupName === rowGroup.name
            ? 0
            : statusTrackerList.rows.filter(
                (x) => x.rowGroupId === row.rowGroupId
              ).length,
        values: this.constructDprStatusTrackerReportCellValues(
          dprStatusTracker,
          statusTrackerList,
          +row.id,
          columnGroupId,
          colorTypes
        ),
      };
      rows.push(rowValue);
    }

    return rows;
  }

  private constructDprStatusTrackerReportCellValues(
    dprStatusTracker: DprStatusTracker,
    statusTrackerList: StatusTrackerWithData,
    rowId: number,
    columnGroupId: number,
    colorTypes: ColorType[]
  ): DprStatusTrackerColumnValuesModel[] {
    const columns: DprStatusTrackerColumnValuesModel[] = [];
    for (const column of statusTrackerList.columns.filter(
      (x) => x.columnGroupId === columnGroupId
    )) {
      const columnData = dprStatusTracker.dprStatusTrackerDetails.find(
        (x) => x.columnId === column.id && x.rowId === rowId
      ) ?? {
        colorTypeId: null,
        value: '',
      };

      let listOptions: SelectionItem[];

      switch (column.dataTypeId) {
        case DataType.List:
          listOptions = cloneDeep(statusTrackerList.columnDataTypeValues || [])
            .filter((st) => st.columnId === column.id)
            .map((x) => ({ id: x.id, name: x.value }));
          break;
        case DataType.Boolean:
          listOptions = buildBooleanValues(column.booleanTerminologyId).map(
            (x) => ({ id: x.id, name: x.value })
          );
          break;
        default:
          listOptions = [];
      }

      const value = getValue(
        columnData.value,
        column.dataTypeId,
        listOptions,
        this.datePipe
      );

      columns.push({
        color:
          colorTypes.find((x) => x.id === columnData.colorTypeId)?.hexCode ??
          null,
        value: value ? String(value) : null,
      });
    }

    return columns;
  }
}
